<template>
    <flex-container
        class="page-editor-top-bar"
        :padding="Size.sm"
        align-items="center"
        justify-content="flex-end"
    >
        <button-text style="margin: 0 1rem" @click="emit('back')">
            <flex-container :gap="Size.sm" align-items="center">
                <svg-icon
                    :icon="IconChevronLeft"
                    :size="IconSize.sm"
                    is-custom-icon
                />
                <span>
                    <strong>Terug</strong>
                </span>
            </flex-container>
        </button-text>

        <flex-container
            size="auto"
            align-items="center"
            :gap="Size.xs"
        >
            <strong>
                Pagina
            </strong>
            <div>/</div>
            <inline-edit-text
                v-model="title"
            />
        </flex-container>

        <div style="flex-grow: 1" />

        <div class="seperator" />

        <button-plain-icon
            v-tooltip="'Ongedaan maken'"
            :icon="IconArrowRotateLeft"
            :size="IconSize.sm"
            is-custom-icon
            @click="emit('undo')"
        />

        <button-plain-icon
            v-tooltip="'Opnieuw'"
            :icon="IconArrowRotateRight"
            :size="IconSize.sm"
            is-custom-icon
            @click="emit('redo')"
        />

        <div class="seperator" />

        <button-plain-icon
            v-tooltip="'Responsive'"
            :icon="IconLaptopMobile"
            :size="IconSize.sm"
            is-custom-icon
            @click="toggleResponsive"
        />

        <div class="seperator" />

        <button-block @loading="save">
            Opslaan
        </button-block>
    </flex-container>
</template>

<script setup lang="ts">
    import { ref, watch } from 'vue';
    import { ButtonState, type ButtonStateChangedEvent, FlexContainer, IconSize, Size, SvgIcon } from '@keyvue/one-ui';
    import IconChevronLeft from '@keyvue/one-awesome-icons/icons/regular/chevron-left.svg?component';
    import IconArrowRotateLeft from '@keyvue/one-awesome-icons/icons/regular/arrow-rotate-left.svg?component';
    import IconArrowRotateRight from '@keyvue/one-awesome-icons/icons/regular/arrow-rotate-right.svg?component';
    import IconLaptopMobile from '@keyvue/one-awesome-icons/icons/regular/laptop-mobile.svg?component';
    import InlineEditText from '@/flow/flow/components/InlineEditText.vue';

    type ScreenMode =
        | 'desktop'
        | 'tablet-landscape'
        | 'tablet'
        | 'mobile'
        | 'fullview'
    ;

    interface Props {
        title: string,
    }

    const props = defineProps<Props>();
    const title = ref(props.title);

    const showResponsive = ref(false);
    function toggleResponsive() {
        showResponsive.value = !showResponsive.value;

        if (showResponsive.value) {
            emit('set-screen-mode', 'mobile');
        } else {
            emit('set-screen-mode', 'fullview');
        }
    }

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'back'): void
        (e: 'undo'): void
        (e: 'redo'): void
        (e: 'set-screen-mode', size: ScreenMode): void
        (e: 'update:title', changedTitle: string): void
    }>();

    function save(event: ButtonStateChangedEvent) {
        emit('save');
        event.button.setState(ButtonState.completed);
    }

    watch(() => title.value, (newValue) => emit('update:title', newValue));
</script>

<style scoped lang="scss">
.page-editor-top-bar {
    border-bottom: 1px solid color(gray-300);

    .seperator {
        border-left: 1px solid color(gray-300);
        height: size(lg);
    }

    button {
        margin: 0 size(sm);
    }
}
</style>
